import { zodResolver } from '@hookform/resolvers/zod'
import { deleteCookie } from 'cookies-next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'

import { EdunodesJoinCommunityDialog } from '@/components/auth/edunodes-join-community-dialog'
import { Button } from '@/components/custom/button'
import { PasswordInput } from '@/components/custom/password-input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import { SESSION_ID_HEADER } from '@/constant/app'
import type { EdunodesCommunitySummary } from '@/constant/edunodes'
import { ADMIN_DEFAULT_HREF } from '@/constant/navItems'
import { useAuth } from '@/contexts/auth-context'
import { useNotification } from '@/contexts/notification-context'
import { hasAdminDashboardAccess } from '@/enums'
import authService from '@/services/auth.service'
import { baseService } from '@/services/base.service'
import { ErrorResponse } from '@/utils/axios-error-handle'
import { isEdunodesCommunitySiteFromWindow } from '@/utils/tenant.utils'

import type { UserProfile } from '@/types/user.type'

type SignInFormProps = React.HTMLAttributes<HTMLDivElement> & {
  disabledSignIn?: boolean
  buttonClassName?: string
}

const formSchema = z.object({
  email: z.string().min(1, { message: 'Please enter your email' }).email({ message: 'Invalid email address' }),
  password: z
    .string()
    .min(1, { message: 'Please enter your password' })
    .min(6, { message: 'Password must be at least 6 characters long' }),
})

type EdunodesJoinOfferState = {
  email: string
  password: string
  community: EdunodesCommunitySummary
}

export function SignInForm({ className, disabledSignIn = true, buttonClassName, ...props }: SignInFormProps) {
  const router = useRouter()
  const [isLoading, setIsLoading] = React.useState(false)
  const [edunodesJoinOffer, setEdunodesJoinOffer] = React.useState<EdunodesJoinOfferState | null>(
    null
  )
  const [joinDialogOpen, setJoinDialogOpen] = React.useState(false)
  const [isJoining, setIsJoining] = React.useState(false)
  const inviteCheckoutParam = router.query['invite-checkout']

  const { login } = useAuth()
  const { toast } = useToast()
  const { refresh } = useNotification()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const completeSignIn = async (
    loginResponse: Awaited<ReturnType<typeof authService.login<UserProfile>>>
  ) => {
    const userSignInResponse = loginResponse.data
    const sessionId = loginResponse.sessionId

    if (!userSignInResponse.isActive) {
      localStorage.setItem('pendingVerifyEmail', userSignInResponse.email.toLowerCase())
      router.push('/auth/verify-account')
      return
    }

    refresh()
    await login(userSignInResponse, sessionId, {
      accessToken: loginResponse.accessToken,
      refreshToken: loginResponse.refreshToken,
      accessTokenExpiresIn: loginResponse.accessTokenExpiresIn,
      refreshTokenExpiresIn: loginResponse.refreshTokenExpiresIn,
    })
    if (inviteCheckoutParam) {
      await router.push({
        pathname: '/buy-now',
        query: { 'invite-checkout': inviteCheckoutParam },
      })
      return
    }
    if (hasAdminDashboardAccess(userSignInResponse.roles)) {
      window.location.href = ADMIN_DEFAULT_HREF
      return
    }
    const previousPage = router.query.previousPage
    const rawDest = typeof previousPage === 'string' ? previousPage : '/'
    const signInPaths = new Set(['/sign-in', '/en/sign-in', '/super-admin/sign-in'])
    const dest = signInPaths.has(rawDest) ? '/' : rawDest
    window.location.href = dest
  }

  const tryEdunodesPlatformJoinOffer = async (
    email: string,
    password: string
  ): Promise<boolean> => {
    if (!isEdunodesCommunitySiteFromWindow()) {
      return false
    }
    try {
      const offer = await authService.verifyEdunodesPlatform({
        email,
        password,
      })
      if (offer?.edunodesJoinCommunity) {
        setEdunodesJoinOffer({
          email: offer.email,
          password,
          community: offer.community,
        })
        setJoinDialogOpen(true)
        return true
      }
    } catch {
      // Fall through to generic login error.
    }
    return false
  }

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true)
    const normalizedEmail = data.email.toLowerCase()
    try {
      const loginResponse = await authService.login<UserProfile>({
        email: normalizedEmail,
        password: data.password,
      })
      await completeSignIn(loginResponse)
    } catch (error) {
      const _error = error as ErrorResponse
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorAny = error as any
      const statusCode = _error.response?.data?.statusCode || errorAny?.response?.status

      deleteCookie(SESSION_ID_HEADER)
      baseService.clearSessionId()

      if (
        (statusCode === 401 || statusCode === 403) &&
        (await tryEdunodesPlatformJoinOffer(normalizedEmail, data.password))
      ) {
        return
      }

      let errorMessage = 'An error occurred. Please try again.'
      if (_error.response?.data?.message) {
        errorMessage = _error.response.data.message
      } else if (statusCode === 401 || statusCode === 403) {
        errorMessage = 'Incorrect email or password'
      } else if (_error.message) {
        errorMessage = _error.message
      }

      toast({
        variant: 'destructive',
        description: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleJoinCommunity = async () => {
    if (!edunodesJoinOffer) {
      return
    }
    setIsJoining(true)
    try {
      const loginResponse = await authService.joinEdunodesCommunity<UserProfile>({
        email: edunodesJoinOffer.email,
        password: edunodesJoinOffer.password,
      })
      setJoinDialogOpen(false)
      setEdunodesJoinOffer(null)
      await completeSignIn(loginResponse)
    } catch (error) {
      const _error = error as ErrorResponse
      toast({
        variant: 'destructive',
        description: _error.response?.data?.message || 'Unable to join this community. Please try again.',
      })
    } finally {
      setIsJoining(false)
    }
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      {edunodesJoinOffer ? (
        <EdunodesJoinCommunityDialog
          open={joinDialogOpen}
          onOpenChange={open => {
            setJoinDialogOpen(open)
            if (!open) {
              setEdunodesJoinOffer(null)
            }
          }}
          community={edunodesJoinOffer.community}
          email={edunodesJoinOffer.email}
          onConfirm={handleJoinCommunity}
          isLoading={isJoining}
        />
      ) : null}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="name@example.com"
                      {...field}
                      // Trim on blur
                      onBlur={e => {
                        field.onChange(e.target.value.trim())
                      }}
                      // Trim pasted text
                      onPaste={e => {
                        e.preventDefault()
                        const pasted = e.clipboardData.getData('Text')
                        field.onChange(pasted.trim())
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <div className="flex items-center justify-between">
                    <FormLabel>Password</FormLabel>
                    <Link
                      href="/auth/forgot-password"
                      className="text-sm font-medium text-muted-foreground hover:opacity-75"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <FormControl>
                    <PasswordInput placeholder="********" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button className={cn('mt-2', buttonClassName)} loading={isLoading} disabled={disabledSignIn}>
              Sign In
            </Button>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
